/**
 * Manuscript Item entity — a collectible lore scroll that appears on the maze floor.
 *
 * When collected, emits a MANUSCRIPT_COLLECTED event so the game can persist
 * the discovery. Manuscripts spawn once per level with a configurable
 * probability (MANUSCRIPT_SPAWN_CHANCE), only for uncollected manuscripts.
 *
 * Visual: uses the TEX_MANUSCRIPT procedural texture (18×18 parchment), with
 * a gentle float and periodic glint to attract the player's attention.
 *
 * @extends Phaser.GameObjects.Sprite
 */

import Phaser from 'phaser';
import { TEX_MANUSCRIPT, TEX_GLOW } from '../utils/constants.js';
import {
  TILE_SIZE,
  AUDIO_MANUSCRIPT_COLLECT,
  COLOR_MANUSCRIPT_PARCHMENT,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class ManuscriptItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene - The scene this manuscript belongs to.
   * @param {number} col - Grid column (expanded grid).
   * @param {number} row - Grid row (expanded grid).
   * @param {number} manuscriptId - The ID (1-30) of the manuscript this item represents.
   */
  constructor(scene, col, row, manuscriptId) {
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    super(scene, x, y, TEX_MANUSCRIPT);

    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {number} Grid column in the expanded grid. */
    this.gridCol = col;

    /** @type {number} Grid row in the expanded grid. */
    this.gridRow = row;

    /** @type {number} The manuscript ID this floor item represents. */
    this.manuscriptId = manuscriptId;

    scene.add.existing(this);
    this.setDepth(5);
    this.setScale(0);

    // --- Pop-in animation ---
    scene.tweens.add({
      targets: this,
      scale: 1.2,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        scene.tweens.add({
          targets: this,
          scale: 1,
          duration: 150,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // --- Gentle float (up/down) ---
    this._floatTween = scene.tweens.add({
      targets: this,
      y: y - 2,
      duration: 1400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // --- Slow rotation (parchment sway) ---
    this._rotateTween = scene.tweens.add({
      targets: this,
      angle: { from: -5, to: 5 },
      duration: 2400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // --- Glow halo behind the manuscript ---
    this._glow = scene.add.graphics();
    this._glow.setDepth(4);
    this._glowAlpha = 0.2;
    this._drawGlow();

    this._glowTween = scene.tweens.add({
      targets: this,
      _glowAlpha: 0.06,
      duration: 1800,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        this._drawGlow();
      },
    });

    // --- Periodic glint ---
    this._scheduleGlint();
  }

  /**
   * Draw the soft glow halo behind the manuscript.
   * @private
   */
  _drawGlow() {
    if (!this._glow || !this.scene) return;
    this._glow.clear();
    this._glow.fillStyle(COLOR_MANUSCRIPT_PARCHMENT, this._glowAlpha);
    this._glow.fillCircle(this.x, this.y, 11);
  }

  /**
   * Schedule the next random glint flash.
   * @private
   */
  _scheduleGlint() {
    if (!this.scene) return;
    const delay = Phaser.Math.Between(5000, 10000);
    this._glintTimer = this.scene.time.delayedCall(delay, () => {
      this._playGlint();
    });
  }

  /**
   * Flash a brief warm glint on the manuscript.
   * @private
   */
  _playGlint() {
    if (!this.scene || !this.active) return;

    // Quick alpha flash
    this.scene.tweens.add({
      targets: this,
      alpha: { from: 1, to: 0.35 },
      duration: 300,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    // Particle burst (warm parchment glow)
    const emitter = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
      lifespan: 350,
      speed: { min: 12, max: 30 },
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.7, end: 0 },
      tint: COLOR_MANUSCRIPT_PARCHMENT,
      frequency: -1,
    });
    emitter.setDepth(6);
    emitter.explode(3);
    this.scene.time.delayedCall(450, () => {
      if (emitter) emitter.destroy();
    });

    this._scheduleGlint();
  }

  /**
   * Collect the manuscript. Plays sound and a collection animation.
   * The caller is responsible for emitting the MANUSCRIPT_COLLECTED event.
   */
  collect() {
    if (!this.active) return;
    this.active = false;

    // Cancel timers
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }

    if (!this.scene) return;

    // Play collection sound
    try {
      this.scene.sound.play(AUDIO_MANUSCRIPT_COLLECT, { volume: getSfxVol(0.6) });
    } catch (_) { /* audio fallback */ }

    // Show collect label
    this._showCollectLabel();

    // Animate collection: scale up + fade
    this.scene.tweens.add({
      targets: this,
      scale: 2.5,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  /**
   * Show a floating "MANUSCRIPT" label above the collection point.
   * @private
   */
  _showCollectLabel() {
    if (!this.scene) return;

    const label = this.scene.add.text(this.x, this.y - 10, 'MANUSCRIPT', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
    })
      .setOrigin(0.5)
      .setDepth(150)
      .setScale(0.5);

    this.scene.tweens.add({
      targets: label,
      scale: 2.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!label.scene) return;
        label.scene.tweens.add({
          targets: label,
          y: label.y - 30,
          alpha: 0,
          duration: 600,
          delay: 300,
          ease: 'Quad.easeOut',
          onComplete: () => {
            label.destroy();
          },
        });
      },
    });
  }

  /**
   * Clean up resources.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    if (this._rotateTween) {
      this._rotateTween.stop();
      this._rotateTween = null;
    }
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glow) {
      this._glow.destroy();
      this._glow = null;
    }
    super.destroy(fromScene);
  }
}
